/* ============================================================
   Per-product bespoke customisation.
   The customised order is charged through two NO-DISCOUNT products so
   that no coupon code can ever apply to a custom order:
     - "Bespoke piece"        : the piece value, in £5 tiers
     - "Bespoke Customisation": the fee = max(min, ceil(pct% of price))
   The £5 base rounding remainder is absorbed into the fee line, so the
   total the customer pays is exact. The real product title + the
   customer's requests are attached as line-item properties.
   ============================================================ */
(function () {
  'use strict';

  function money(cents) {
    var n = parseInt(cents, 10);
    if (isNaN(n)) return '';
    try {
      if (window.theme && typeof window.theme.formatMoney === 'function') {
        return window.theme.formatMoney(n, window.theme.moneyFormat || '£{{amount}}');
      }
    } catch (e) {}
    return '£' + (n / 100).toFixed(2);
  }

  var mapCache = {}; // handle -> Promise<{amount:variantId}>
  function getAmountMap(handle) {
    if (mapCache[handle]) return mapCache[handle];
    mapCache[handle] = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        var map = {};
        (p.variants || []).forEach(function (v) {
          var amt = parseInt(v.option1, 10);
          if (!isNaN(amt)) map[amt] = v.id;
        });
        return map;
      })
      .catch(function () { return {}; });
    return mapCache[handle];
  }

  function getProductData(root) {
    var script = document.querySelector('[data-product-json]');
    if (script) {
      try {
        var data = JSON.parse(script.textContent);
        if (data && data.variants) return Promise.resolve(data);
      } catch (e) {}
    }
    return fetch(root.getAttribute('data-product-url') + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  function currentVariantId(root) {
    var formId = root.getAttribute('data-form-id');
    var form = formId ? document.getElementById(formId) : null;
    if (form && form.elements && form.elements.namedItem('id')) {
      return parseInt(form.elements.namedItem('id').value, 10);
    }
    var input = document.querySelector('[name="id"]');
    return input ? parseInt(input.value, 10) : null;
  }

  function variantPrice(productData, variantId) {
    if (!productData || !productData.variants) return null;
    for (var i = 0; i < productData.variants.length; i++) {
      if (productData.variants[i].id === variantId) return productData.variants[i].price;
    }
    return productData.variants[0] ? productData.variants[0].price : null;
  }

  function clampFee(n) { return Math.min(100, Math.max(40, n)); }

  function lockScroll(on) {
    document.documentElement.style.overflow = on ? 'hidden' : '';
    document.body.style.overflow = on ? 'hidden' : '';
  }

  function init(root) {
    if (root.__pcustInit) return;
    root.__pcustInit = true;

    var pct = parseFloat(root.getAttribute('data-fee-pct')) || 30;
    var min = parseFloat(root.getAttribute('data-fee-min')) || 40;
    var feeHandle = root.getAttribute('data-fee-handle');
    var baseHandle = root.getAttribute('data-base-handle');
    var title = root.getAttribute('data-product-title');

    var modal = root.querySelector('[data-pcust-modal]');
    var openBtn = root.querySelector('[data-pcust-open]');
    var addBtn = root.querySelector('[data-pcust-add]');
    var errEl = root.querySelector('[data-pcust-error]');
    var baseEl = root.querySelector('[data-pcust-base]');
    var feeEl = root.querySelector('[data-pcust-fee]');
    var totalEl = root.querySelector('[data-pcust-total]');

    var state = { baseVariantId: null, feeVariantId: null };

    function recompute() {
      return Promise.all([getProductData(root), getAmountMap(feeHandle), getAmountMap(baseHandle)]).then(function (res) {
        var productData = res[0], feeMap = res[1], baseMap = res[2];
        var vid = currentVariantId(root);
        var priceCents = variantPrice(productData, vid);
        if (priceCents == null) return;

        var pounds = Math.round(priceCents / 100);
        var realFee = clampFee(Math.max(min, Math.ceil((pct / 100) * pounds)));
        var roundedBase = Math.max(5, Math.floor(pounds / 5) * 5);
        var feeAmount = clampFee(pounds - roundedBase + realFee);

        state.baseVariantId = baseMap[roundedBase] || null;
        state.feeVariantId = feeMap[feeAmount] || null;

        if (baseEl) baseEl.textContent = money(priceCents);
        if (feeEl) feeEl.textContent = money(realFee * 100);
        if (totalEl) totalEl.textContent = money((pounds + realFee) * 100);
      });
    }

    function openModal() {
      recompute();
      modal.hidden = false;
      lockScroll(true);
      var first = modal.querySelector('[data-pcust-field]');
      if (first) setTimeout(function () { first.focus(); }, 50);
    }
    function closeModal() {
      modal.hidden = true;
      lockScroll(false);
      if (errEl) errEl.hidden = true;
    }

    openBtn.addEventListener('click', openModal);
    root.querySelectorAll('[data-pcust-close]').forEach(function (el) { el.addEventListener('click', closeModal); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !modal.hidden) closeModal(); });

    addBtn.addEventListener('click', function () {
      if (errEl) errEl.hidden = true;

      var details = {};
      var any = false;
      root.querySelectorAll('[data-pcust-field]').forEach(function (f) {
        var val = (f.value || '').trim();
        if (val) { details['Customisation – ' + f.getAttribute('data-pcust-field')] = val; any = true; }
      });
      if (!any) {
        if (errEl) { errEl.textContent = 'Please tell us at least one change you would like.'; errEl.hidden = false; }
        return;
      }
      if (!state.baseVariantId || !state.feeVariantId) {
        if (errEl) { errEl.textContent = 'Something went wrong. Please refresh and try again.'; errEl.hidden = false; }
        return;
      }

      var baseProps = Object.assign({ 'Piece': title, 'Customisation': 'Yes' }, details);

      addBtn.classList.add('is-loading');
      addBtn.setAttribute('disabled', 'disabled');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            { id: state.baseVariantId, quantity: 1, properties: baseProps },
            { id: state.feeVariantId, quantity: 1, properties: { 'For': title } }
          ]
        })
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, body: d }; }); })
        .then(function (res) {
          addBtn.classList.remove('is-loading');
          addBtn.removeAttribute('disabled');
          if (!res.ok) {
            if (errEl) { errEl.textContent = (res.body && res.body.description) || 'Something went wrong. Please try again.'; errEl.hidden = false; }
            return;
          }
          closeModal();
          if (window.theme && window.theme.settings && window.theme.settings.cartType === 'drawer') {
            document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
            document.dispatchEvent(new CustomEvent('theme:cart-drawer:open'));
          } else {
            window.location.href = '/cart';
          }
        })
        .catch(function () {
          addBtn.classList.remove('is-loading');
          addBtn.removeAttribute('disabled');
          if (errEl) { errEl.textContent = 'Something went wrong. Please try again.'; errEl.hidden = false; }
        });
    });
  }

  function boot() { document.querySelectorAll('[data-pcust]').forEach(init); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelectorAll) e.target.querySelectorAll('[data-pcust]').forEach(init);
  });
})();
